import { getTrackPosForIndex } from './utils/metrics.js';

/**
 * Manages track transitions
 * Converts slide indexes to track positions
 * Tells animator to go to new position (via animation or jump)
 * Relays and emits events back to the carousel
 *
 **/
export default class TransitionManager {
	constructor(ctx, animator) {
		const _ = this;

		_.ctx = ctx;
		_.animator = animator;

		_.handlers = {
			movementRequested: ({ index, pageIndex, trackPosition, velocity, movementType }) => {
				if (trackPosition !== undefined) {
					// Direct track position request (continuous positioning)
					_.goToTrackPosition(trackPosition, velocity, movementType);
				} else if (index !== undefined) {
					// Set state for frame-based rendering
					_.ctx.store.setState({
						renderIndex: index,
						pageIndex: pageIndex,
					});
					// Slide index request (discrete positioning)
					_.goToSlide(index, velocity, movementType);
				}
			},
		};

		_.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		const { emitter, events } = this.ctx;

		// Listen for movement requests from the main carousel
		emitter.on(events.movement.requested, this.handlers.movementRequested);
	}

	// calculates position on track and tells animator to go
	goToSlide(slideIndex, velocity, movementType) {
		const _ = this;
		const widths = _.ctx.store.getWidths();
		const options = _.ctx.store.getOptions();
		const state = _.ctx.store.getState();
		// Convert slide index to track position and delegate to goToTrackPosition
		_.goToTrackPosition(
			getTrackPosForIndex(slideIndex, widths, options, state),
			velocity,
			movementType
		);
	}

	// Direct track position movement for continuous positioning
	goToTrackPosition(newPos, velocity, movementType) {
		const _ = this;

		// Handle jump immediately - no path calculation needed
		if (movementType === 'jump') {
			_.animator.goToPosition(newPos, 0, 'jump', 0);
			return;
		}

		const currentPos = _.animator.currentPos;
		const widths = _.ctx.store.getWidths();
		const trackWidth = widths.track;

		// Apply looping path logic when loop is enabled
		if (_.ctx.store.getState().canLoop) {
			const pageCount = _.ctx.store.getState().pageCount;
			const hasVelocity = velocity !== undefined && velocity !== 0;

			// Special case: 2-page loops always follow velocity direction
			if (pageCount <= 2 && hasVelocity) {
				const wantsForward = velocity < 0;

				if (wantsForward && newPos > currentPos) {
					newPos -= trackWidth;
				} else if (!wantsForward && newPos <= currentPos) {
					newPos += trackWidth;
				}
			} else {
				// Original distance-based logic
				const posDelta = Math.abs(currentPos - newPos);
				const halfTrack = trackWidth / 2;

				if (posDelta === halfTrack) {
					// Tie-breaker: use velocity if available
					if (hasVelocity) {
						if (velocity < 0 && newPos > currentPos) {
							newPos -= trackWidth;
						} else if (velocity > 0 && newPos <= currentPos) {
							newPos += trackWidth;
						}
					} else if (newPos > currentPos) {
						newPos -= trackWidth;
					}
				} else if (posDelta > halfTrack) {
					// Take shortest path
					if (newPos >= currentPos) {
						newPos -= trackWidth;
					} else {
						newPos += trackWidth;
					}
				}
				// posDelta < halfTrack: already on short path, no change
			}
		}

		// Apply default velocity if none provided
		if (velocity === undefined) {
			velocity = newPos < currentPos ? -15 : 15;
		} else {
			// boost velocity a bit
			velocity *= 1.2;
			if (Math.abs(velocity) < 15) velocity *= 1.3;
		}

		// Calculate direction for animator
		let direction = 0;
		if (newPos < currentPos) {
			direction = -1;
		} else if (newPos > currentPos) {
			direction = 1;
		}

		_.animator.goToPosition(newPos, velocity, movementType, direction);
	}

	/**
	 * Destroy the transition manager and clean up event listeners
	 */
	destroy() {
		const _ = this;
		const { emitter, events } = _.ctx;

		// Clean up event listeners
		emitter.off(events.movement.requested, _.handlers.movementRequested);

		// Clear references for garbage collection
		_.ctx = null;
		_.animator = null;
		_.handlers = null;
	}
}
